/**
 * WI-F1 — Runtime tenant config service (ADO #38529).
 *
 * Runtime tenant configuration fetched by hostname from the backend
 * `GET /api/v1/config` endpoint (backend US-7.5 / ADO-38499 / PR 16064).
 *
 * This module is ADDITIVE. It does NOT replace the existing eager
 * `authConfig.ts` / `config/environment.ts` config, `MsalInstanceManager`,
 * or the `main.tsx` boot sequence — those conversions are WI-F2/F3/F4,
 * which will consume this service. Nothing here is wired into boot yet.
 *
 * The `/config` endpoint is `@Public()` / unauthenticated, secret-free,
 * resolves the tenant from the `?domain=` query param (with `Origin` /
 * `Referer` fallback) and returns HTTP 404 for an unknown/inactive tenant.
 */

/**
 * E-sign configuration for the resolved tenant.
 *
 * ALL fields are nullable until a tenant onboards e-sign. Provider-selection
 * (WI-F7) must handle a null `provider` gracefully.
 */
export interface TenantESignConfig {
  provider: 'okta' | 'entra' | null;
  issuer: string | null;
  clientId: string | null;
  /**
   * How the BACKEND will prove multi-factor authentication on the signature
   * token (middleware WI-9). This decides the ceremony, so it cannot be
   * inferred client-side:
   *
   * - `none` / `amr` — the plain `prompt=login` re-auth popup is sufficient.
   *   Under `amr` the backend additionally requires the token's `amr` claim to
   *   contain `mfa`, which Entra emits only when a second factor was actually
   *   completed. Nothing extra is asked of this client.
   * - `acrs` — the token must carry a Conditional Access authentication
   *   context, which requires a claims-challenge round trip this client does
   *   NOT implement. See {@link acrsValue}.
   */
  mfaMode: 'none' | 'amr' | 'acrs';
  /**
   * The auth-context id the claims challenge must request. Non-null only when
   * `mfaMode` is `acrs`.
   */
  acrsValue: string | null;
}

/** Identity metadata for the resolved tenant. */
export interface TenantIdentity {
  uuid: string;
  slug: string;
  displayName: string;
}

/**
 * Regional formatting for the resolved tenant. Always populated — the
 * `tenant_settings` columns backing this block are `NOT NULL DEFAULT`
 * (`UTC` / `yyyy-MM-dd` / `HH:mm`), unlike `esign`'s nullable fields.
 *
 * `modifiedAtUtc` is the tenant_settings row's concurrency token. There is
 * no `GET /tenant-settings` (removed — read-only via this endpoint now), so
 * this is the ONLY place the SPA gets a fresh token to send back as
 * `expectedModifiedAtUtc` on the next `PATCH /tenant-settings` save.
 */
export interface TenantRegionalConfig {
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  modifiedAtUtc: string;
}

/**
 * Typed shape of the landed `GET /api/v1/config` response.
 *
 * This is a SUPERSET of the originally-assumed shape:
 * - `audience` MAY be `api://<clientId>` — do NOT assume `audience === clientId`.
 * - `scopes` is server-provided (e.g. `["<clientId>/.default"]`) — prefer it
 *   over deriving scopes client-side.
 * - `tenant` carries identity metadata for the resolved tenant.
 * - `esign` is nullable field-by-field (null until the tenant onboards e-sign).
 * - `regional` is always populated (server-side defaults backfill it).
 * - `apiBaseUrl` is the dedicated API host for cross-origin tenants (ADO-38573).
 *   Empty string means same-origin.
 */
export interface TenantConfig {
  /** e.g. https://login.microsoftonline.com/<tid>/v2.0 */
  authority: string;
  clientId: string;
  /** MAY be api://<clientId> — do NOT assume it equals clientId. */
  audience: string;
  /** Server-provided, e.g. ["<clientId>/.default"]. Prefer over client-side derivation. */
  scopes: string[];
  /** e.g. https://<domain> */
  redirectUri: string;
  /**
   * Dedicated API host for this tenant (ADO-38573). Empty string = same-origin.
   * Set by `setTenantConfigApiBase` at boot; read by `getTenantConfigApiBase`.
   */
  apiBaseUrl: string;
  tenant: TenantIdentity;
  esign: TenantESignConfig;
  regional: TenantRegionalConfig;
}

/** Discriminated result of {@link fetchTenantConfig}. */
export type TenantConfigResult =
  | { status: 'success'; config: TenantConfig }
  /** Backend returned 404 for an unknown/inactive domain. */
  | { status: 'tenant-not-found' }
  /** Network error, non-404 failure after retries, or an unparseable body. */
  | { status: 'config-unavailable'; reason: string };

export interface FetchTenantConfigOptions {
  /**
   * Number of attempts on transient (network / 5xx) failures.
   * Defaults to 3. Non-transient outcomes (200, 404, malformed body) do
   * not retry.
   */
  attempts?: number;
  /** Base delay (ms) for the bounded backoff between retries. Defaults to 300. */
  backoffBaseMs?: number;
  /** Injectable for tests; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Injectable for tests; defaults to `window.location.hostname`. */
  hostname?: string;
  /**
   * Optional abort signal. When it fires (e.g. the boot-timeout in
   * {@link fetchTenantConfigWithTimeout} calls `controller.abort()`), the
   * in-flight request is aborted and the result is a terminal
   * `config-unavailable` — an aborted request is NOT retried.
   */
  signal?: AbortSignal;
}

// ---------------------------------------------------------------------------
// apiBase derivation (ADO-38573)
// ---------------------------------------------------------------------------
//
// The API base is NOT a build-time host: tenants live on per-tenant subdomains
// behind Front Door, so a single baked-in `NX_API_BASE_URL` cannot serve every
// tenant. The runtime value comes from `GET /api/v1/config` → `apiBaseUrl`
// (set by `setTenantConfigApiBase` in main.tsx after a successful config fetch).
//
// Default is SAME-ORIGIN (empty string → relative `/api/v1/config?...`).
// Older backends that don't return `apiBaseUrl` stay on same-origin — no hard
// failure. Empty string = same-origin; this is the ONE place the convention is
// applied. Do NOT hardcode a tenant host anywhere else.
let _apiBase = '';

/**
 * Set the runtime API base URL from the `/config` response.
 * Called once at boot (before `initAxios`) with `TenantConfig.apiBaseUrl`.
 * Empty string preserves same-origin behaviour for tenants without a dedicated
 * API host or when the backend does not yet return the field.
 */
export const setTenantConfigApiBase = (url: string): void => {
  _apiBase = url;
};

/** Read the current runtime API base. Empty string = same-origin. */
export const getTenantConfigApiBase = (): string => _apiBase;

const CONFIG_PATH = '/api/v1/config';
const DEFAULT_ATTEMPTS = 3;
const DEFAULT_BACKOFF_BASE_MS = 300;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const isStringOrNull = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

/**
 * Defensive parse of the raw `/config` JSON body into a {@link TenantConfig}.
 *
 * Returns `null` when a required field is missing/renamed or the shape is
 * otherwise wrong — the caller treats `null` as config-unavailable rather than
 * silently accepting a partial config. `esign` is nullable field-by-field.
 */
export const parseTenantConfig = (body: unknown): TenantConfig | null => {
  if (typeof body !== 'object' || body === null) {
    return null;
  }
  const raw = body as Record<string, unknown>;

  if (
    !isNonEmptyString(raw.authority) ||
    !isNonEmptyString(raw.clientId) ||
    !isNonEmptyString(raw.audience) ||
    !isNonEmptyString(raw.redirectUri)
  ) {
    return null;
  }

  if (
    !Array.isArray(raw.scopes) ||
    !raw.scopes.every((scope) => typeof scope === 'string')
  ) {
    return null;
  }

  const tenant = raw.tenant as Record<string, unknown> | undefined;
  if (
    typeof tenant !== 'object' ||
    tenant === null ||
    !isNonEmptyString(tenant.uuid) ||
    !isNonEmptyString(tenant.slug) ||
    !isNonEmptyString(tenant.displayName)
  ) {
    return null;
  }

  const esign = raw.esign as Record<string, unknown> | undefined;
  if (typeof esign !== 'object' || esign === null) {
    return null;
  }
  const provider = esign.provider;
  if (provider !== 'okta' && provider !== 'entra' && provider !== null) {
    return null;
  }
  if (!isStringOrNull(esign.issuer) || !isStringOrNull(esign.clientId)) {
    return null;
  }
  // `mfaMode` is tolerated as ABSENT rather than required: a backend predating
  // WI-9 does not send it, and hard-failing the parse would take the whole app
  // down at boot over a field only the e-sign flow reads. An absent value means
  // "no MFA assertion", which is exactly what those backends do. An
  // UNRECOGNISED value IS fatal though — silently downgrading an unknown future
  // mode to `none` would run a weaker ceremony than the tenant configured.
  const mfaMode = esign.mfaMode ?? 'none';
  if (mfaMode !== 'none' && mfaMode !== 'amr' && mfaMode !== 'acrs') {
    return null;
  }
  if (!isStringOrNull(esign.acrsValue ?? null)) {
    return null;
  }

  const regional = raw.regional as Record<string, unknown> | undefined;
  if (
    typeof regional !== 'object' ||
    regional === null ||
    !isNonEmptyString(regional.timezone) ||
    !isNonEmptyString(regional.dateFormat) ||
    !isNonEmptyString(regional.timeFormat) ||
    !isNonEmptyString(regional.modifiedAtUtc)
  ) {
    return null;
  }

  return {
    authority: raw.authority,
    clientId: raw.clientId,
    audience: raw.audience,
    scopes: raw.scopes as string[],
    redirectUri: raw.redirectUri,
    apiBaseUrl: typeof raw.apiBaseUrl === 'string' ? raw.apiBaseUrl : '',
    tenant: {
      uuid: tenant.uuid,
      slug: tenant.slug,
      displayName: tenant.displayName,
    },
    esign: {
      provider,
      issuer: esign.issuer,
      clientId: esign.clientId,
      mfaMode,
      acrsValue: (esign.acrsValue as string | null) ?? null,
    },
    regional: {
      timezone: regional.timezone,
      dateFormat: regional.dateFormat,
      timeFormat: regional.timeFormat,
      modifiedAtUtc: regional.modifiedAtUtc,
    },
  };
};

const buildConfigUrl = (hostname: string): string => {
  const base = getTenantConfigApiBase();
  const query = `?domain=${encodeURIComponent(hostname)}`;
  return `${base}${CONFIG_PATH}${query}`;
};

/**
 * Fetch the runtime {@link TenantConfig} for the current hostname.
 *
 * Resolves to a discriminated {@link TenantConfigResult} so WI-F4 can show
 * distinct UI for each case:
 * - `success`            → parsed config.
 * - `tenant-not-found`   → HTTP 404 (unknown/inactive domain). Not retried.
 * - `config-unavailable` → network error / non-404 status after retries, or a
 *                          malformed/renamed body. `reason` is diagnostic only.
 *
 * Transient failures (network error / 5xx) are retried with a small bounded
 * backoff. A 404 and a malformed body are terminal (no retry).
 */
export const fetchTenantConfig = async (
  options: FetchTenantConfigOptions = {}
): Promise<TenantConfigResult> => {
  const {
    attempts = DEFAULT_ATTEMPTS,
    backoffBaseMs = DEFAULT_BACKOFF_BASE_MS,
    fetchImpl = typeof fetch !== 'undefined' ? fetch : undefined,
    hostname = typeof window !== 'undefined' ? window.location.hostname : '',
    signal,
  } = options;

  if (!fetchImpl) {
    return {
      status: 'config-unavailable',
      reason: 'No fetch implementation available in this environment.',
    };
  }

  const url = buildConfigUrl(hostname);
  const totalAttempts = Math.max(1, attempts);
  let lastReason = 'Unknown error while fetching tenant config.';

  // An already-aborted signal is terminal before we even start.
  if (signal?.aborted) {
    return {
      status: 'config-unavailable',
      reason: 'Tenant config fetch was aborted.',
    };
  }

  for (let attempt = 1; attempt <= totalAttempts; attempt++) {
    let response: Response;
    try {
      response = await fetchImpl(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal,
      });
    } catch (error) {
      // An abort is terminal — do NOT retry (the boot timeout has fired, or the
      // caller cancelled deliberately).
      if (
        signal?.aborted ||
        (error instanceof Error && error.name === 'AbortError')
      ) {
        return {
          status: 'config-unavailable',
          reason: 'Tenant config fetch was aborted.',
        };
      }
      // Network / transient failure → retry.
      lastReason =
        error instanceof Error ? error.message : 'Network request failed.';
      if (attempt < totalAttempts) {
        await delay(backoffBaseMs * attempt);
        continue;
      }
      break;
    }

    // 404 → unknown/inactive tenant. Terminal, distinct case.
    if (response.status === 404) {
      return { status: 'tenant-not-found' };
    }

    // 5xx → transient server failure → retry.
    if (response.status >= 500) {
      lastReason = `Server responded with ${response.status}.`;
      if (attempt < totalAttempts) {
        await delay(backoffBaseMs * attempt);
        continue;
      }
      break;
    }

    // Any other non-OK status → terminal config-unavailable.
    if (!response.ok) {
      return {
        status: 'config-unavailable',
        reason: `Config request failed with status ${response.status}.`,
      };
    }

    // 2xx → parse defensively.
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      return {
        status: 'config-unavailable',
        reason: 'Config response was not valid JSON.',
      };
    }

    const config = parseTenantConfig(body);
    if (!config) {
      return {
        status: 'config-unavailable',
        reason: 'Config response was missing or had malformed fields.',
      };
    }

    return { status: 'success', config };
  }

  return { status: 'config-unavailable', reason: lastReason };
};
